import React from 'react';
import { Link } from 'react-router-dom';
import { useScrollToTop } from '../../hooks/useScrollToTop';

const Footer: React.FC = () => {
  const scrollToTop = useScrollToTop();

  return (
    <footer className="bg-midnight-blue pt-20 pb-10 border-t border-white/10 text-on-surface-variant font-sans">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Column */}
          <div className="col-span-1">
            <Link to="/" onClick={scrollToTop} className="block mb-6">
              <img
                src="/assets/logobg.png"
                alt="CloudYatra"
                className="h-16 md:h-20 w-auto object-contain"
              />
            </Link>
            <p className="mb-6 leading-relaxed text-slate-400">
              Navigate · Migrate · Elevate. Practitioner-led cloud migration, managed services, cost optimization, security, website support, and AWS/Azure guidance.
            </p>
            <div className="flex space-x-4">
              {[
                { icon: 'bi-linkedin', url: 'https://www.linkedin.com/company/cloudyatra-private-limited' },
                { icon: 'bi-twitter-x', url: 'https://x.com/YatraCloud' },
                { icon: 'bi-discord', url: 'https://discord.gg/fVpRAceNgq' }
              ].map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-electric-blue hover:text-white transition-all duration-300"
                >
                  <i className={`bi ${social.icon}`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div className="col-span-1">
            <h5 className="text-white font-display font-semibold text-lg mb-6">Services</h5>
            <ul className="space-y-3">
              {[
                { name: 'AWS Consulting', path: '/services/aws-consulting' },
                { name: 'Azure Consulting', path: '/services/azure-consulting' },
                { name: 'Cloud Migration', path: '/services/cloud-migration' },
                { name: 'Managed Services', path: '/services/managed-services' },
                { name: 'Cost Optimization', path: '/services/cloud-cost-optimization-finops' },
                { name: 'Security & Governance', path: '/services/cloud-security-governance' },
                { name: 'GenAI Readiness', path: '/services/genai-readiness' },
                { name: 'DevOps & Automation', path: '/services/devops-automation' },
                { name: 'Backup & Recovery', path: '/services/backup-recovery' },
                { name: 'Cloud Credit Facility', path: '/contact' },
              ].map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    onClick={scrollToTop}
                    className="text-slate-400 hover:text-electric-blue transition-colors duration-300 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions & Company */}
          <div className="col-span-1">
            <h5 className="text-white font-display font-semibold text-lg mb-6">Solutions</h5>
            <ul className="space-y-3 mb-8">
              {[
                { name: 'For Startups', path: '/solutions/startups' },
                { name: 'For SMBs & MSMEs', path: '/solutions/smb-msme' },
                { name: 'For Enterprises', path: '/solutions/enterprises' },
              ].map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    onClick={scrollToTop}
                    className="text-slate-400 hover:text-electric-blue transition-colors duration-300 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            <h5 className="text-white font-display font-semibold text-lg mb-4">Company</h5>
            <ul className="space-y-3">
              {[
                { name: 'About Us', path: '/about' },
                { name: 'Noida Office', path: '/locations/noida' },
                { name: 'Lucknow Office', path: '/locations/lucknow' },
                { name: 'Migration Calculator', path: '/resources/cloud-migration-calculator' },
                { name: 'Resources', path: '/resources' },
                { name: 'Contact', path: '/contact' },
              ].map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    onClick={scrollToTop}
                    className="text-slate-400 hover:text-electric-blue transition-colors duration-300 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="col-span-1">
            <h5 className="text-white font-display font-semibold text-lg mb-6">Contact Us</h5>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <i className="bi bi-envelope text-electric-blue mt-1"></i>
                <a href="mailto:sales@cloudyatra.co.in" className="text-slate-400 hover:text-white transition-colors text-sm">sales@cloudyatra.co.in</a>
              </div>
              <div className="flex items-start space-x-3">
                <i className="bi bi-telephone text-electric-blue mt-1"></i>
                <a href="tel:+917667529020" className="text-slate-400 hover:text-white transition-colors text-sm">+91 7667529020</a>
              </div>
              <div className="flex items-start space-x-3">
                <i className="bi bi-geo-alt text-electric-blue mt-1"></i>
                <span className="text-slate-400 text-sm">Lucknow & Noida, India</span>
              </div>
              <div className="pt-3">
                <a href="/CloudYatra_Final.pdf" className="inline-flex items-center text-sm font-semibold text-white hover:text-electric-blue transition-colors">
                  <i className="bi bi-download mr-2" aria-hidden="true"></i>
                  Download Company Profile
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 text-center text-sm text-slate-500">
          <p>&copy; {new Date().getFullYear()} CloudYatra. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
